import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Shield, Zap, Code, Lock } from "lucide-react";
import learnMoreHero from "@/assets/learn-more-hero.jpg";

const LearnMore = () => {
  return (
    <Layout>
      <SEO 
        title="How It Works - Understanding Cryptographic Random Generation"
        description="Learn how our cryptographically secure random number generator works using the Web Crypto API. Understand true randomness and why it matters."
        canonical="https://numbers-generator.com/learn-more"
        keywords="how random generator works, cryptographic randomness, Web Crypto API, secure RNG"
      />
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Numbers Generator - Learn How It Works
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Understanding cryptographic randomness and how RandomGen works
          </p>
        </header>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* What is RandomGen */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-4">What is RandomGen?</h2>
            <p className="text-muted-foreground mb-4">
              RandomGen is a modern, web-based random number generator that uses cryptographically
              secure methods to generate truly random values. Unlike traditional pseudo-random
              number generators, our platform leverages the Web Crypto API to provide
              security-grade randomness suitable for both casual and professional applications.
            </p>
            <p className="text-muted-foreground">
              Whether you need to roll dice for a game, generate secure passwords, pick random
              winners, or create lottery numbers, RandomGen provides fast, reliable, and provably
              random results.
            </p>
          </Card>

          {/* How It Works */}
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <Code className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">How It Works</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>
                RandomGen uses the{" "}
                <code className="bg-secondary px-2 py-1 rounded">
                  crypto.getRandomValues()
                </code>{" "}
                method from the Web Crypto API. This method accesses the operating system's
                cryptographically secure pseudo-random number generator (CSPRNG).
              </p>
              <div className="bg-secondary rounded-lg p-4 font-mono text-sm">
                <code>
                  const array = new Uint32Array(1);
                  <br />
                  crypto.getRandomValues(array);
                  <br />
                  const randomNumber = array[0];
                </code>
              </div>
              <p>
                The underlying CSPRNG uses hardware entropy sources combined with cryptographic
                algorithms to ensure that the generated values are unpredictable and suitable for
                security-sensitive applications.
              </p>
            </div>
          </Card>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <Shield className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Cryptographically Secure</h3>
              <p className="text-muted-foreground">
                Uses industry-standard cryptographic methods suitable for security applications,
                password generation, and fair gaming.
              </p>
            </Card>

            <Card className="p-6">
              <Zap className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-2">Lightning Fast</h3>
              <p className="text-muted-foreground">
                Generate thousands of random values instantly. No server-side processing means
                immediate results.
              </p>
            </Card>

            <Card className="p-6">
              <Lock className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2">Privacy First</h3>
              <p className="text-muted-foreground">
                All generation happens locally in your browser. Your data never leaves your
                device unless you choose to share it.
              </p>
            </Card>

            <Card className="p-6">
              <Code className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-2">Open & Transparent</h3>
              <p className="text-muted-foreground">
                Our methods are based on open web standards. You can inspect the code and verify
                the randomness yourself.
              </p>
            </Card>
          </div>

          {/* Use Cases */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-6">Common Use Cases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-l-4 border-primary pl-4">
                <h4 className="font-bold mb-1">Gaming</h4>
                <p className="text-sm text-muted-foreground">
                  Dice rolls, card shuffling, and fair random events
                </p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h4 className="font-bold mb-1">Security</h4>
                <p className="text-sm text-muted-foreground">
                  Password generation, token creation, and cryptographic keys
                </p>
              </div>
              <div className="border-l-4 border-primary pl-4">
                <h4 className="font-bold mb-1">Education</h4>
                <p className="text-sm text-muted-foreground">
                  Random student selection, quiz questions, group assignments
                </p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h4 className="font-bold mb-1">Contests</h4>
                <p className="text-sm text-muted-foreground">
                  Raffle drawings, lottery numbers, winner selection
                </p>
              </div>
              <div className="border-l-4 border-primary pl-4">
                <h4 className="font-bold mb-1">Development</h4>
                <p className="text-sm text-muted-foreground">
                  Test data, unique IDs, random sampling
                </p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h4 className="font-bold mb-1">Research</h4>
                <p className="text-sm text-muted-foreground">
                  Statistical simulations, random trials, data sampling
                </p>
              </div>
            </div>
          </Card>

          {/* FAQ */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h4 className="font-bold mb-2">Is the randomness truly random?</h4>
                <p className="text-muted-foreground">
                  While technically pseudo-random (as all computer-generated randomness is), the
                  cryptographic methods we use are indistinguishable from true randomness for all
                  practical purposes and meet NIST standards for random number generation.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-2">Can I use this for cryptographic purposes?</h4>
                <p className="text-muted-foreground">
                  Yes! The Web Crypto API is specifically designed for cryptographic applications
                  and is suitable for generating passwords, tokens, and other security-sensitive
                  values.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-2">Is my data stored anywhere?</h4>
                <p className="text-muted-foreground">
                  No. All random generation happens locally in your browser. We don't store or
                  transmit any of your generated values unless you explicitly choose to save them
                  with an account.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-2">How is this different from Math.random()?</h4>
                <p className="text-muted-foreground">
                  JavaScript's Math.random() is not cryptographically secure and can be
                  predictable. We use crypto.getRandomValues() which provides much stronger
                  randomness suitable for security applications.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={learnMoreHero} 
            alt="Educational cryptography and security learning resources showing encryption, code snippets, and technology concepts for understanding random number generation"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default LearnMore;
